import { Component, signal, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgStyle } from '@angular/common';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { CommonService, Exception, ToasterService, Validator } from '@anddone/ui-utilities';
import { Notification } from '../../../../../services/notification';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { MessageConstants } from '../../../../../constants/message.constant';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-add-email',
  imports: [ReactiveFormsModule, NgStyle, TranslateModule],
  templateUrl: './add-email.html',
  styleUrl: './add-email.scss',
})
export class AddEmail implements OnInit {
  isLoader = signal<boolean>(false);
  distributionListForm!: FormGroup;
  distributionListFormErrors: any = {};
  selectedNotification: any;
  validator: Validator;
  addedEmails!: any[];
  errorFlag = true;
  distributionId: any = undefined;

  private formBuilder = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  public notificationService = inject(Notification);
  public commonService = inject(CommonService);
  public allyCommonService = inject(AllyCommonService);
  private toasterService = inject(ToasterService);
  constructor() {
    this.validator = new Validator({});
  }

  ngOnInit(): void {
    this.selectedNotification = this.notificationService.getNotificationRow();
    this.distributionId = this.selectedNotification?.source._value.distributionList?.id;
    this.distributionListForm = this.formBuilder.group({
      inputEmail: ['', []],
    });

    this.distributionListForm.valueChanges.subscribe((data) =>
      this.distributionListValueChanged(data)
    );
  }

  distributionListValueChanged(_data?: any) {
    if (!this.distributionListForm) {
      return;
    }
    this.distributionListFormErrors = this.validator.validate(this.distributionListForm);
  }

  emailAddress() {
    let emailText = this.distributionListForm.value.inputEmail.trim();
    if (emailText.length <= 0) {
      this.errorFlag = true;
      this.distributionListFormErrors.inputEmail = 'Please enter an email.';
      this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
      return;
    }
    const emails = emailText.split(/[,\n;]/);
    const newEmails = [];

    for (let i = 0; i < emails.length; i++) {
      const email = emails[i].trim();
      if (email.length == 0) {
        this.errorFlag = true;
        this.distributionListFormErrors.inputEmail = 'Please enter an email.';
        this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
      } else if (email.length > 0) {
        if (email.length > 50) {
          this.errorFlag = true;
          this.distributionListFormErrors.inputEmail =
            'Email cannot be more than 50 digits/characters.';
          this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
          return;
        } else if (this.validateEmail(email) && !this.isEmailExists(newEmails, email)) {
          newEmails.push(email);
          this.errorFlag = false;
        } else {
          // Handle invalid email or duplicate email error
          if (!this.validateEmail(email)) {
            this.errorFlag = true;
            this.distributionListFormErrors.inputEmail = 'Email is not valid';
            this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
            return;
          } else if (this.validateEmail(email)) {
            this.errorFlag = true;
            this.distributionListFormErrors.inputEmail = 'Email is already present';
            this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
            return;
          } else if (this.validateEmail(email) && this.isEmailExists(newEmails, email)) {
            this.errorFlag = true;
            this.distributionListFormErrors.inputEmail = 'Duplicate email';
            this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
            return;
          } else {
            this.errorFlag = true;
            this.distributionListForm.get('inputEmail')?.setErrors({ emailInvalid: true });
          }
        }
      } else {
        this.errorFlag = false;
      }
    }
    emailText = newEmails.join('\n');
    this.addedEmails = [...newEmails];
  }

  isEmailExists(array: string[], email: string) {
    if (array.length > 0) {
      const isEmail = array.some((x) => x.toLowerCase() == email.toLowerCase());
      return isEmail;
    } else {
      return false;
    }
  }

  validateEmail(email: string) {
    const re = ValidationConstant.email_regex;
    return re.test(email);
  }

  close() {
    this.activeModal.close();
  }

  addEmailAddress() {
    this.isLoader.set(true);
    const obj = {
      EmailList: this.addedEmails?.toString(),
    };
    if (this.distributionId == undefined) {
      this.notificationService
        .addDistributionList(this.selectedNotification?.source._value.id, obj)
        .subscribe({
          next: () => {
            if (!this.selectedNotification?.source._value.isPaused) {
              if (this.selectedNotification?.source._value.status == 'Default') {
                this.subscribeDefaultNotification(this.selectedNotification?.source._value.id);
              } else if (
                this.selectedNotification?.source._value.distributionList?.emailCount == 0 ||
                this.selectedNotification?.source._value.distributionList?.emailCount == undefined
              ) {
                this.subscribeAllyNotification();
              }
            } else {
              this.isLoader.set(false);
              this.activeModal.close('Modal closed');
              this.toasterService.showSuccess(MessageConstants.notification.emailsAdded, 'full');
              this.notificationService.setEmailAdded();
            }
          },
          error: (e) => {
            this.isLoader.set(false);
            this.activeModal.close('Modal closed');
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    } else if (this.distributionId) {
      this.notificationService
        .updateDistributionList(
          this.selectedNotification?.source._value.id,
          this.distributionId,
          obj
        )
        .subscribe({
          next: () => {
            if (!this.selectedNotification?.source._value.isPaused) {
              if (this.selectedNotification?.source._value.status == 'Default') {
                this.subscribeDefaultNotification(this.selectedNotification?.source._value.id);
              } else if (
                this.selectedNotification?.source._value.distributionList?.emailCount == 0 ||
                this.selectedNotification?.source._value.distributionList?.emailCount == undefined
              ) {
                this.subscribeAllyNotification();
              } else {
                this.isLoader.set(false);
                this.activeModal.close('Modal closed');
                this.toasterService.showSuccess(
                  MessageConstants.notification.emailsUpdated,
                  'full'
                );
                this.notificationService.setEmailUpdated();
              }
            } else {
              this.isLoader.set(false);
              this.activeModal.close('Modal closed');
              this.toasterService.showSuccess(MessageConstants.notification.emailsUpdated, 'full');
              this.notificationService.setEmailUpdated();
            }
          },
          error: (e) => {
            this.isLoader.set(false);
            this.activeModal.close('Modal closed');
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    }
  }

  subscribeDefaultNotification(item: any) {
    this.notificationService.subscribeDefaultNotification(item).subscribe({
      next: () => {
        this.isLoader.set(false);
        this.activeModal.close('Modal closed');
        this.toasterService.showSuccess(MessageConstants.notification.emailsAdded, 'full');
        this.notificationService.setEmailAdded();
      },
      error: (e: any) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  subscribeAllyNotification() {
    const obj = {
      status: 'Active',
    };
    this.notificationService
      .subscribeAllyNotification(this.selectedNotification?.source._value?.id, obj)
      .subscribe({
        next: () => {
          this.isLoader.set(false);
          this.activeModal.close('Modal closed');
          this.toasterService.showSuccess(MessageConstants.notification.emailsAdded, 'full');
          this.notificationService.setEmailAdded();
        },
        error: (e) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }
}
